<template>
  <div class="console-pills">
    <!-- The clicked Notes pill expands a plain-text area here, right below the
         query input (above the pills row). Stop clicks/mousedowns from
         bubbling to the console root, whose `@click` refocuses the query input
         and would steal the caret. -->
    <div
      v-if="expandedNotesProperty"
      class="console-notes-region"
      @mousedown.stop
      @click.stop
    >
      <textarea
        ref="notesTextareaEl"
        v-model="notesText"
        class="console-notes-input"
        rows="2"
        :placeholder="`Add ${expandedNotesProperty.name.toLowerCase()}…`"
        @input="onNotesInput"
      />
    </div>
    <div
      v-if="context || orderedProperties.length"
      class="console-property-pills"
    >
      <!-- Attached capture context (browser tab, file, app): a read-only
         chip — submitting via the footer's Capture button stores it on the
         created object, the Create button ignores it. Enabling the plugin
         when it is disabled goes through the footer's greyed-out Capture
         button, not the pill. -->
      <span
        v-if="context"
        class="pill pill--context pill--set"
        @mousedown.stop
        @mouseenter="onContextPillEnter"
        @mouseleave="hideTooltip"
      >
        <BaseContextIcon :context="contextIcon" class="pill-context-icon" />
        <span class="pill-context-text">{{ contextText }}</span>
      </span>
      <template v-for="property in visibleProperties" :key="property.id">
        <!-- Select / multi-select properties reuse the graph `GraphSelect`
           editor in commit mode: the pill is its trigger, and the shared
           commit machinery serializes the chosen value(s). -->
        <div
          v-if="property.type === 'Select'"
          class="pill pill--property pill--editor"
          :class="{ 'pill--set': labelFor(property) }"
          @mousedown.stop
          @click.capture="onPillClickCapture(property, $event)"
          @click.stop="toggleEditor(property.id)"
          @mouseenter="onPropertyPillEnter(property, $event)"
          @mouseleave="hideTooltip"
        >
          <component :is="iconFor(property)" class="pill-icon" />
          <!-- Once a value is picked it becomes the pill's label: the property
             name disappears and a permanent cross takes over from
             GraphSelect's hover-only clear button. -->
          <span v-if="!labelFor(property)" class="pill-name">{{
            property.name
          }}</span>
          <GraphSelect
            :ref="editorRef(property.id)"
            class="pill-graph-select"
            state="inline"
            :name="property.name"
            :type="choicesFor(property)"
            :data="selectDataFor(property)"
            :list="property.list"
            :optional="property.optional"
            :show-placeholder="false"
            :show-clear-button="false"
            @update-fields="
              (_name, value) => $emit('update-select', property, value)
            "
          />
          <span
            v-if="showClear(property)"
            class="pill-clear"
            :title="`Clear ${property.name}`"
            @mousedown.stop.prevent
            @click.stop="onClearClick(property)"
          >
            <CloseIcon class="pill-clear-icon" />
          </span>
        </div>
        <!-- Relation / multi-relation properties reuse the graph `GraphRelation`
           editor (the same one the create modal uses); it loads candidate
           objects on demand via its own server-side query. Wrapped in Suspense
           because its setup is async and the console tree has no boundary. -->
        <div
          v-else-if="property.type === 'Relation' && relationTypeFor(property)"
          class="pill pill--property pill--editor"
          :class="{ 'pill--set': labelFor(property) }"
          @mousedown.stop
          @click.capture="onPillClickCapture(property, $event)"
          @click.stop="toggleEditor(property.id)"
          @mouseenter="onPropertyPillEnter(property, $event)"
          @mouseleave="hideTooltip"
        >
          <component :is="iconFor(property)" class="pill-icon" />
          <span class="pill-name">{{ property.name }}</span>
          <Suspense>
            <GraphRelation
              :ref="editorRef(property.id)"
              class="pill-graph-select"
              state="inline"
              :name="property.name"
              :type="relationTypeFor(property)"
              :data="relationDataFor(property)"
              :list="property.list"
              :optional="property.optional"
              :show-placeholder="false"
              :show-collection="false"
              :show-create-new="false"
              :show-open-button="false"
              :show-clear-button="!isParserFilled(property)"
              @update-fields="
                (_name, value) => $emit('update-relation', property, value)
              "
            />
          </Suspense>
        </div>
        <!-- Boolean properties reuse the graph `GraphBool` editor: the checkbox
           sits where other pills show their icon, and clicking anywhere on the
           pill (name or checkbox) flips the value. -->
        <div
          v-else-if="property.type === 'Boolean'"
          class="pill pill--property pill--editor pill--bool"
          :class="{ 'pill--set': boolDataFor(property) }"
          @click.capture="onPillClickCapture(property, $event)"
          @click.stop="toggleBool(property)"
        >
          <!-- The checkbox toggles itself and emits the new value; stop the click
             here so the pill's own toggle doesn't fire a second time. -->
          <span class="pill-graph-bool" @click.stop>
            <GraphBool
              state="inline"
              :name="property.name"
              :data="boolDataFor(property)"
              @update-fields="
                (_name, value) => $emit('update-bool', property, value)
              "
            />
          </span>
          <span class="pill-name">{{ property.name }}</span>
        </div>
        <!-- Text-based properties (text/email/phone/url) reuse the graph
           `GraphString` editor. The pill shows the name/value until clicked,
           then reveals an inline input; blurring commits and collapses it. -->
        <div
          v-else-if="isTextProperty(property)"
          class="pill pill--property pill--editor pill--text"
          :class="{ 'pill--set': labelFor(property) }"
          @click.stop="startInputEdit(property)"
        >
          <component :is="iconFor(property)" class="pill-icon" />
          <GraphString
            v-if="editingInputId === property.id"
            :ref="setInputEditorRef"
            class="pill-graph-string"
            state="inline"
            :name="property.name"
            :data="textDataFor(property)"
            :format="stringFormatFor(property)"
            :optional="property.optional"
            :show-placeholder="false"
            :show-clear-button="false"
            @update-fields="
              (_name, value) => $emit('update-text', property, value)
            "
            @blur="stopInputEdit"
          />
          <template v-else>
            <span class="pill-name">{{ property.name }}</span>
            <span v-if="labelFor(property)" class="pill-text-value">{{
              labelFor(property)
            }}</span>
            <span
              v-if="showClear(property)"
              class="pill-clear"
              :title="`Clear ${property.name}`"
              @mousedown.stop.prevent
              @click.stop="onClearClick(property)"
            >
              <CloseIcon class="pill-clear-icon" />
            </span>
          </template>
        </div>
        <!-- Number properties reuse the graph `GraphNumber` editor with the same
           click-to-edit / blur-to-collapse flow as text pills. -->
        <div
          v-else-if="property.type === 'Number'"
          class="pill pill--property pill--editor pill--text"
          :class="{ 'pill--set': labelFor(property) }"
          @click.stop="startInputEdit(property)"
        >
          <component :is="iconFor(property)" class="pill-icon" />
          <GraphNumber
            v-if="editingInputId === property.id"
            :ref="setInputEditorRef"
            class="pill-graph-string"
            state="inline"
            :name="property.name"
            :data="numberDataFor(property)"
            :column-type="columnTypeFor(property)"
            :optional="property.optional"
            :show-placeholder="false"
            @update-fields="
              (_name, value) => $emit('update-number', property, value)
            "
            @blur="stopInputEdit"
          />
          <template v-else>
            <span class="pill-name">{{ property.name }}</span>
            <span v-if="labelFor(property)" class="pill-text-value">{{
              labelFor(property)
            }}</span>
            <span
              v-if="showClear(property)"
              class="pill-clear"
              :title="`Clear ${property.name}`"
              @mousedown.stop.prevent
              @click.stop="onClearClick(property)"
            >
              <CloseIcon class="pill-clear-icon" />
            </span>
          </template>
        </div>
        <!-- Notes properties don't edit inline: the pill toggles a full-width
           text area rendered right below the query input (see the region
           before this list). -->
        <div
          v-else-if="property.type === 'Notes'"
          class="pill pill--property pill--editor"
          :class="{
            'pill--set': labelFor(property),
            'pill--active': expandedNotesId === property.id,
          }"
          @click.stop="toggleNotes(property)"
        >
          <component :is="iconFor(property)" class="pill-icon" />
          <span class="pill-name">{{ property.name }}</span>
          <span
            v-if="showClear(property)"
            class="pill-clear"
            :title="`Clear ${property.name}`"
            @mousedown.stop.prevent
            @click.stop="onClearClick(property)"
          >
            <CloseIcon class="pill-clear-icon" />
          </span>
        </div>
        <button
          v-else
          type="button"
          class="pill pill--property"
          :class="{ 'pill--set': labelFor(property) }"
          @mousedown.stop.prevent
          @click.stop="onPropertyClick(property, $event)"
          @mouseenter="onPropertyPillEnter(property, $event)"
          @mouseleave="hideTooltip"
        >
          <component :is="iconFor(property)" class="pill-icon" />
          <span class="pill-name">{{
            labelFor(property) ?? property.name
          }}</span>
          <span
            v-if="showClear(property)"
            class="pill-clear"
            :title="`Clear ${property.name}`"
            @mousedown.stop.prevent
            @click.stop="onClearClick(property)"
          >
            <CloseIcon class="pill-clear-icon" />
          </span>
        </button>
      </template>
      <!-- Overflow menu: with more than the visible cap of properties, the
           extra ones fold into a "…" pill whose menu lists them; picking one
           reveals it as a pill so it can be edited in place. -->
      <ButtonMenu
        v-if="overflowProperties.length > 0"
        class="pill-overflow"
        placement="bottom-start"
        :offset="[0, 4]"
      >
        <template #btn>
          <span
            class="pill pill--property pill--more"
            title="More properties"
            @mousedown.stop.prevent
          >
            <MoreIcon class="pill-more-icon" />
          </span>
        </template>
        <template #content>
          <BaseButton
            v-for="property in overflowProperties"
            :key="property.id"
            kind="auxiliary"
            type="tertiary"
            :text="property.name"
            @click="revealProperty(property)"
          >
            <template #icon>
              <component :is="iconFor(property)" />
            </template>
          </BaseButton>
        </template>
      </ButtonMenu>
    </div>
  </div>
</template>

<script lang="ts">
import { format } from "date-fns";
import {
  Component,
  ComponentPublicInstance,
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  PropType,
  ref,
  watch,
  watchEffect,
} from "vue";

import type {
  GraphDatabase,
  GraphRelationTarget,
  GraphResult,
  GraphStringFormat,
  Week,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseContextIcon from "@/components/base/BaseContextIcon.vue";
import { getIcon } from "@/components/context";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import {
  availableDefinitionTypes,
  PropertyTypeName,
} from "@/components/graph/definition/graph-definition";
import type {
  GraphRelationResult,
  GraphRelationType,
} from "@/components/graph/graph-relation";
import GraphBool from "@/components/graph/GraphBool.vue";
import GraphNumber from "@/components/graph/GraphNumber.vue";
import GraphString from "@/components/graph/GraphString.vue";
import type { TypePropertyInfo } from "@/components/graph/introspection";
import GraphRelation from "@/components/graph/properties/GraphRelation.vue";
import GraphSelect from "@/components/graph/properties/GraphSelect.vue";
import { getTitleFieldName } from "@/components/graph/title";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import TextIcon from "@/components/icons/TextIcon.vue";
import * as inject from "@/components/inject";
import { useTooltip, type TooltipContent } from "@/components/tooltip";
import type { Context } from "@/types";
import { captureContextDisplayText } from "@/utils/context";

type GraphSelectChoices = Array<{ value: string; type: "string_const" }>;

/**
 * A value a console pill holds until the object is created. Extend this union
 * as more property types become editable inline.
 */
export type PropertyValue =
  | { type: "Date"; date: Date }
  // A week batch, only offered by the task "Planned for" pill (a week
  // postpones the task instead of scheduling it on a day).
  | { type: "Week"; week: Week }
  | { type: "Select"; value: string | string[] }
  | { type: "Boolean"; value: boolean }
  | { type: "Text"; value: string }
  | { type: "Number"; value: number }
  | { type: "Notes"; text: string }
  | {
      type: "Relation";
      value: GraphRelationResult | GraphRelationResult[];
      label: string;
    }
  // Relation targets referenced from an input keyword (e.g. `#label`): the
  // parser only knows their id and name. The pill synthesizes the records to
  // display from these (see `relationDataFor`), so the value looks the same
  // as one picked through the relation editor.
  | { type: "RelationRefs"; refs: Array<{ id: string; name: string }> };

/**
 * Where a pill's value came from, which drives its affordances:
 * - "explicit": set through the pill's own editor; shows a clear cross.
 * - "parser": derived from an input keyword (e.g. `on <date>`); read-only,
 *   it can only be changed by editing the input.
 * - "default": the type's default (e.g. an event's duration); editable like
 *   an unset pill, but there is nothing to clear back to, so no clear cross.
 */
export type PillValueSource = "explicit" | "parser" | "default";

/** A pill's current value together with its provenance. */
export type PillValue = { value: PropertyValue; source: PillValueSource };

// Icon per canonical property type, reused from the graph definition catalog so
// pills stay consistent with the rest of the schema UI.
const iconByType: Partial<Record<PropertyTypeName, Component>> =
  Object.fromEntries(availableDefinitionTypes.map((d) => [d.text, d.icon]));

export default defineComponent({
  name: "ConsolePills",
  components: {
    BaseButton,
    BaseContextIcon,
    ButtonMenu,
    CloseIcon,
    GraphBool,
    GraphNumber,
    GraphRelation,
    GraphSelect,
    GraphString,
    MoreIcon,
  },
  props: {
    type: { type: Object as PropType<GraphDatabase>, required: true },
    properties: {
      type: Array as PropType<TypePropertyInfo[]>,
      default: () => [],
    },
    // The pills' current values with their provenance, keyed by property id.
    // Everything shown on a pill (label, inline editor data, clearability) is
    // derived from this single map.
    values: {
      type: Object as PropType<Record<string, PillValue>>,
      default: () => ({}),
    },
    // The attached capture context (contextual capture), rendered as a
    // read-only pill ahead of the property pills; null when nothing is
    // attached or the selected type can't store it.
    context: {
      type: Object as PropType<Context | null>,
      default: null,
    },
  },
  emits: {
    "select-property": (_property: TypePropertyInfo, _element?: HTMLElement) =>
      true,
    "clear-property": (_property: TypePropertyInfo) => true,
    "update-select": (_property: TypePropertyInfo, _value: GraphResult) => true,
    "update-relation": (_property: TypePropertyInfo, _value: GraphResult) =>
      true,
    "update-bool": (_property: TypePropertyInfo, _value: GraphResult) => true,
    "update-text": (_property: TypePropertyInfo, _value: GraphResult) => true,
    "update-number": (_property: TypePropertyInfo, _value: GraphResult) => true,
    "update-notes": (_property: TypePropertyInfo, _value: string) => true,
  },
  setup(props, { emit }) {
    // Provide the commit-mode context expected by graph property editors
    // (e.g. GraphSelect): no object exists yet, so writes are collected as
    // draft field updates (emitted via `updateFields`) rather than persisted.
    inject.autosave(ref(false));
    inject.objectId(ref(undefined));
    inject.database(computed<GraphDatabase | undefined>(() => props.type));

    const workspaceDatabases = inject.workspaceDatabases();
    const controller = inject.controller();
    // Locale-aware "Week N" label for a week-valued Planned for pill.
    const locale = inject.locale();

    // Possible relation targets for the selected type's workspace, enumerated
    // by the controller. Built-in targets (e.g. "task") aren't part of
    // `workspaceDatabases`, so this is the only place they can be resolved
    // from. Re-fetched when the type pill switches to a type in another
    // workspace (the component instance persists across type switches).
    const relationTargets = ref<GraphRelationTarget[]>([]);
    watch(
      () => props.type.workspace,
      (workspace) => {
        void controller.graph.relationTargets(workspace).then((targets) => {
          // Drop a stale response if the workspace changed again meanwhile.
          if (props.type.workspace === workspace)
            relationTargets.value = targets;
        });
      },
      { immediate: true }
    );

    const choicesFor = (property: TypePropertyInfo): GraphSelectChoices =>
      (property.values ?? []).map((option) => ({
        type: "string_const",
        value: option.value,
      }));

    // Resolve a relation property's target table (a custom `TableId` or a
    // built-in kind like "task") to a `GraphDatabase`, then wrap it in the
    // shape `GraphRelation` expects. `GraphRelation` reads the target's name
    // from this value to build its candidate query, so resolution is required;
    // when the target can't be resolved the pill falls back to a plain,
    // read-only pill (see the `v-else-if` guard in the template).
    const relationTypeFor = (
      property: TypePropertyInfo
    ): GraphRelationType | undefined => {
      const target = property.relationTarget;
      if (!target) return undefined;
      const db = resolveRelationTarget(target);
      if (!db) return undefined;
      return { id: db.id, database: { type: { type: "const", value: db } } };
    };

    // A relation target is stored either as a custom table id (e.g.
    // `table:…`) or as a built-in kind (e.g. `task`). Custom targets carry a
    // full database in `workspaceDatabases`; built-in types don't appear there,
    // so resolve them from the workspace's relation targets (whose `id` is the
    // same kind string) and synthesize the minimal database `GraphRelation`
    // needs — it only reads `name`/`data_name`/`meta` and queries by name.
    const resolveRelationTarget = (
      target: string
    ): GraphDatabase | undefined => {
      const fromWorkspace =
        workspaceDatabases.value?.[props.type.workspace]?.[target];
      if (fromWorkspace) return fromWorkspace;
      const builtin = relationTargets.value.find((t) => t.id === target);
      if (!builtin) return undefined;
      return {
        id: builtin.id,
        name: builtin.name,
        data_name: builtin.data_name,
        description: "",
        meta: builtin.meta,
        type: { fields: {}, type: "record" },
        permissions: {},
        origin: "builtin_type",
        workspace: props.type.workspace,
      };
    };

    const iconFor = (property: TypePropertyInfo): Component =>
      iconByType[property.type] ?? TextIcon;

    const elementFrom = (event: MouseEvent): HTMLElement | undefined =>
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : undefined;

    // One shared tooltip whose target element and content follow the hovered
    // property pill; `useTooltip` rebinds when the target changes.
    const tooltipEl = ref<HTMLElement | undefined>(undefined);
    const tooltipContent = ref<TooltipContent>(undefined);
    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(tooltipEl, tooltipContent, "top");
    onBeforeUnmount(releaseTooltip);
    const showPillTooltip = (event: MouseEvent, content: TooltipContent) => {
      const element = elementFrom(event);
      if (!element) return;
      tooltipEl.value = element;
      tooltipContent.value = content;
      showTooltip();
    };
    // Pills whose value has a parser keyword teach it on hover: dates
    // (`in`/`on`), and the default types' Labels (`#`) and Priority (`!`)
    // extension pills — identified by name, the same way the parsed keyword
    // values are folded onto them (see Console.vue's `keywordValues`). The
    // other generic pills stay tooltip-less.
    const onPropertyPillEnter = (
      property: TypePropertyInfo,
      event: MouseEvent
    ) => {
      if (property.type === "Date") {
        showPillTooltip(event, { text: "Set a date", shortcut: "in / on" });
      } else if (property.name === "Labels") {
        showPillTooltip(event, { text: "Set labels", shortcut: "#" });
      } else if (property.name === "Priority") {
        showPillTooltip(event, { text: "Set a priority", shortcut: "!" });
      }
    };

    // The context pill's icon (favicon / app icon) and one-line text, shared
    // with the action rows' second line.
    const contextIcon = computed(() => getIcon(props.context));
    const contextText = computed<string>(() =>
      props.context ? captureContextDisplayText(props.context) : ""
    );
    // The pill's hover tooltip: the full source of the context (URL, file
    // path, app name), which the one-line display text may truncate or
    // replace with the selection.
    const contextTooltip = computed<string | undefined>(() => {
      const context = props.context;
      if (!context) return undefined;
      if (context.category === "browser") return context.url;
      if (context.category === "file") return context.path ?? context.fileName;
      return context.appName;
    });
    const onContextPillEnter = (event: MouseEvent) => {
      if (contextTooltip.value) showPillTooltip(event, contextTooltip.value);
    };

    const valueFor = (property: TypePropertyInfo): PropertyValue | undefined =>
      props.values[property.id]?.value;

    // The human-readable label shown on a set pill. Booleans have no label
    // (their checkbox is the display).
    const labelFor = (property: TypePropertyInfo): string | undefined => {
      const value = valueFor(property);
      if (!value) return undefined;
      switch (value.type) {
        case "Date":
          return format(value.date, "MMMM d");
        case "Week":
          return locale.value.formatWeek(value.week);
        case "Select":
          return Array.isArray(value.value)
            ? value.value.join(", ")
            : value.value;
        case "Relation":
          return value.label;
        case "RelationRefs":
          return value.refs.map((ref) => ref.name).join(", ");
        case "Text":
          return value.value;
        case "Number":
          return String(value.value);
        case "Notes":
          return value.text;
        case "Boolean":
          return undefined;
      }
    };

    // Raw values fed back into the inline editors, so they reflect the
    // current (or default) value and survive pill re-renders.
    const selectDataFor = (property: TypePropertyInfo) => {
      const value = valueFor(property);
      return value?.type === "Select" ? value.value : undefined;
    };
    const relationDataFor = (property: TypePropertyInfo) => {
      const value = valueFor(property);
      if (value?.type === "Relation") return value.value;
      // Parser-set references carry only id/name: synthesize the record shape
      // `GraphRelation`/`BaseRelation` display (title under the target's title
      // field, `_routine` for the id and the type icon), so the pill looks the
      // same as one set through the editor.
      if (value?.type === "RelationRefs") {
        const target = property.relationTarget;
        const database = target ? resolveRelationTarget(target) : undefined;
        if (!database) return undefined;
        const titleField = getTitleFieldName(database) ?? "Title";
        const records = value.refs.map((ref) => ({
          [titleField]: ref.name,
          _routine: { id: ref.id, database },
        }));
        return property.list ? records : records[0];
      }
      return undefined;
    };
    const boolDataFor = (property: TypePropertyInfo) => {
      const value = valueFor(property);
      return value?.type === "Boolean" ? value.value : undefined;
    };
    const textDataFor = (property: TypePropertyInfo) => {
      const value = valueFor(property);
      return value?.type === "Text" ? value.value : undefined;
    };
    const numberDataFor = (property: TypePropertyInfo) => {
      const value = valueFor(property);
      return value?.type === "Number" ? value.value : undefined;
    };
    const notesDataFor = (property: TypePropertyInfo) => {
      const value = valueFor(property);
      return value?.type === "Notes" ? value.text : undefined;
    };

    // A pill whose value came from a parser keyword (e.g. `on <date>`) is
    // read-only: its clear cross is hidden and clicking it must not open an
    // editor. It can only be changed by editing the input.
    const isParserFilled = (property: TypePropertyInfo): boolean =>
      props.values[property.id]?.source === "parser";

    // The pill-level click handlers all guard on `isParserFilled`, but the
    // embedded editors also register their own native triggers (the
    // relation/select value is its popup's trigger element, the bool checkbox
    // toggles itself). Block clicks at the pill boundary in the capture phase
    // so they never reach those triggers.
    const onPillClickCapture = (
      property: TypePropertyInfo,
      event: MouseEvent
    ) => {
      if (isParserFilled(property)) event.stopPropagation();
    };

    // Whether the pill offers its clear cross: it has a value that was set
    // explicitly (not a parser keyword, not a type default).
    const showClear = (property: TypePropertyInfo): boolean =>
      !!labelFor(property) && props.values[property.id]?.source === "explicit";

    const onPropertyClick = (property: TypePropertyInfo, event: MouseEvent) => {
      hideTooltip();
      if (isParserFilled(property)) return;
      emit("select-property", property, elementFrom(event));
    };

    const onClearClick = (property: TypePropertyInfo) =>
      emit("clear-property", property);

    // Flip a boolean pill from the pill body (the embedded checkbox handles its
    // own clicks and emits the toggled value directly).
    const toggleBool = (property: TypePropertyInfo) => {
      if (isParserFilled(property)) return;
      emit("update-bool", property, !boolDataFor(property));
    };

    // The string properties GraphString handles inline.
    const isTextProperty = (property: TypePropertyInfo): boolean =>
      property.type === "Text" ||
      property.type === "Email" ||
      property.type === "Phone" ||
      property.type === "URL";
    // Map a text property to GraphString's `format` (drives icon, link display,
    // and validation); plain text has no format.
    const stringFormatFor = (
      property: TypePropertyInfo
    ): GraphStringFormat | null => {
      if (property.type === "Email") return "email";
      if (property.type === "Phone") return "phone";
      if (property.type === "URL") return "uri";
      return null;
    };

    // GraphNumber truncates input for integer columns and allows decimals for
    // floats; introspection tells us which. Default to float when unknown so we
    // never silently drop a fractional value.
    const columnTypeFor = (property: TypePropertyInfo): "int" | "float" =>
      property.numberType ?? "float";

    // Which input pill (text or number) is currently in edit mode (only one at a
    // time). Clicking such a pill reveals its inline GraphString/GraphNumber
    // input and focuses it; blurring collapses back to the name/value label. A
    // function ref (rather than a string ref) is used because the editor lives
    // inside a `v-for`, where string refs collect into an array.
    const editingInputId = ref<string | undefined>(undefined);
    const inputEditorEl = ref<Element | ComponentPublicInstance | null>(null);
    const setInputEditorRef = (
      el: Element | ComponentPublicInstance | null
    ) => {
      inputEditorEl.value = el;
    };
    const startInputEdit = (property: TypePropertyInfo) => {
      if (isParserFilled(property)) return;
      editingInputId.value = property.id;
      void nextTick(() => {
        const el = inputEditorEl.value;
        if (el && "focus" in el && typeof el.focus === "function") el.focus();
      });
    };
    const stopInputEdit = () => {
      editingInputId.value = undefined;
    };

    // Notes don't edit in place: clicking a Notes pill toggles a full-width
    // plain-text area below the pills row (one at a time). The rich editor can't
    // run in the console window (it needs main-app context), so notes are kept
    // as plain text here and turned into a document when the object is created.
    const notesTextareaEl = ref<HTMLTextAreaElement | null>(null);
    const notesText = ref("");
    const expandedNotesId = ref<string | undefined>(undefined);
    const expandedNotesProperty = computed<TypePropertyInfo | undefined>(() =>
      props.properties.find((p) => p.id === expandedNotesId.value)
    );
    const toggleNotes = (property: TypePropertyInfo) => {
      if (isParserFilled(property)) return;
      const opening = expandedNotesId.value !== property.id;
      expandedNotesId.value = opening ? property.id : undefined;
      if (opening) {
        notesText.value = notesDataFor(property) ?? "";
        void nextTick(() => notesTextareaEl.value?.focus());
      }
    };
    const onNotesInput = () => {
      const property = expandedNotesProperty.value;
      if (property) emit("update-notes", property, notesText.value);
    };
    // The text area is a view of the parent's Notes value: when that value is
    // dropped from outside (the console resets on submit/escape/background
    // click, a type switch, the pill's cross) while the area still shows
    // text, collapse and empty it — this component instance survives those
    // resets, and the stale text would otherwise be re-emitted wholesale on
    // the next keystroke, attaching the previous object's notes to the new
    // one. Whitespace-only text is stored as "no value" by the parent, so it
    // is left alone (the user may be typing a leading space or newline).
    watch(
      () => {
        const property = expandedNotesProperty.value;
        return property ? notesDataFor(property) : undefined;
      },
      (text) => {
        if (text !== undefined) {
          if (text !== notesText.value) notesText.value = text;
        } else if (notesText.value.trim() !== "") {
          notesText.value = "";
          expandedNotesId.value = undefined;
        }
      }
    );

    // A property counts as "set" when it has a value to show: most types expose
    // a display label; booleans have no label, so fall back to their raw value.
    const isSet = (property: TypePropertyInfo): boolean =>
      !!labelFor(property) || !!boolDataFor(property);
    // Pills ordered with the set ones first, then the rest, keeping each group
    // in the schema's original order (stable sort). Held in a ref and only
    // recomputed while no inline editor is open, so committing a value mid-edit
    // doesn't make the focused pill jump; the reorder happens once editing ends.
    const orderedProperties = ref<TypePropertyInfo[]>([]);
    watchEffect(() => {
      if (editingInputId.value) return;
      orderedProperties.value = [...props.properties].sort(
        (a, b) => Number(isSet(b)) - Number(isSet(a))
      );
    });
    // At most this many property pills show inline; the rest fold into the
    // "…" overflow menu. Sized so the default Task set (Planned for, Priority,
    // Project, Labels, Starred, Notes) fits without overflowing.
    const MAX_VISIBLE_PILLS = 6;
    // Properties pulled out of the overflow menu by hand. Reset when the pill
    // set changes (type switch), so a new type starts folded again.
    const revealedIds = ref<Set<string>>(new Set());
    watch(
      () => props.properties,
      () => {
        revealedIds.value = new Set();
      }
    );
    // A pill stays visible when it is within the cap, holds a value (set
    // pills sort first and always carry information), or was explicitly
    // revealed from the overflow menu.
    const visibleProperties = computed<TypePropertyInfo[]>(() => {
      const ordered = orderedProperties.value;
      if (ordered.length <= MAX_VISIBLE_PILLS) return ordered;
      return ordered.filter(
        (property, index) =>
          index < MAX_VISIBLE_PILLS ||
          isSet(property) ||
          revealedIds.value.has(property.id)
      );
    });
    const overflowProperties = computed<TypePropertyInfo[]>(() => {
      const visible = new Set(
        visibleProperties.value.map((property) => property.id)
      );
      return orderedProperties.value.filter(
        (property) => !visible.has(property.id)
      );
    });
    // Picking a property in the overflow menu reveals its pill (the menu
    // hides itself on click); the pill is then edited in place like any other.
    const revealProperty = (property: TypePropertyInfo) => {
      revealedIds.value = new Set([...revealedIds.value, property.id]);
    };

    // An inline editor pill (`GraphSelect`/`GraphRelation`) only makes its value
    // area clickable, so clicking the pill's icon/name would otherwise do
    // nothing. We keep each editor instance (via template refs, since the pills
    // are a `v-for`) and forward pill clicks to its public `togglePopup`; clicks
    // landing directly on the trigger stop propagation there, so the pill
    // handler never double-fires.
    //
    // We read the editor's exposed API (`togglePopup`/`isPopupOpen`) lazily, at
    // click time — never at ref time. Reason: `GraphRelation` has an async
    // `setup` and lives inside `<Suspense>`, so its template ref fires once with
    // a not-yet-resolved instance (methods not exposed yet) and does NOT re-fire
    // when setup resolves (only on some later unrelated re-render). The
    // public-instance proxy is live, though, so holding the instance and reading
    // the methods at click time (setup long resolved by then) is timing-proof.
    type EditorEl = Element | ComponentPublicInstance;
    const editors = new Map<string, EditorEl>();
    const registerEditor = (id: string, el: EditorEl | null) => {
      if (el === null) {
        editors.delete(id);
      } else {
        editors.set(id, el);
      }
    };
    // Stable per-id ref callback so Vue only fires it on mount/unmount instead
    // of re-invoking a fresh closure every render.
    const editorRefs = new Map<string, (el: EditorEl | null) => void>();
    const editorRef = (id: string) => {
      let fn = editorRefs.get(id);
      if (!fn) {
        fn = (el) => registerEditor(id, el);
        editorRefs.set(id, fn);
      }
      return fn;
    };
    // Resolve the editor's exposed API off its live instance proxy, now.
    const editorApi = (id: string) => {
      const el = editors.get(id);
      if (
        el &&
        "togglePopup" in el &&
        typeof el.togglePopup === "function" &&
        "isPopupOpen" in el &&
        typeof el.isPopupOpen === "function"
      ) {
        const toggle = el.togglePopup;
        const isOpen = el.isPopupOpen;
        return {
          toggle: () => toggle.call(el),
          isOpen: () => isOpen.call(el) === true,
        };
      }
      return undefined;
    };

    // Which pill's popup was open when the current click began. Tippy closes an
    // open popup on outside-click during the capture phase — before our pill
    // `@click` runs — so by click time the editor already reports "closed" and a
    // naive toggle would immediately reopen it. We snapshot the open editor on
    // `mousedown` in the capture phase (registered here, before Tippy mounts, so
    // it runs first) to tell "this click closes the popup" from "this click
    // opens it".
    let openEditorIdAtMousedown: string | undefined;
    const onDocumentMousedownCapture = () => {
      openEditorIdAtMousedown = undefined;
      for (const id of editors.keys()) {
        if (editorApi(id)?.isOpen()) {
          openEditorIdAtMousedown = id;
          break;
        }
      }
    };
    document.addEventListener("mousedown", onDocumentMousedownCapture, {
      capture: true,
    });
    onBeforeUnmount(() =>
      document.removeEventListener("mousedown", onDocumentMousedownCapture, {
        capture: true,
      })
    );

    const toggleEditor = (id: string) => {
      hideTooltip();
      if (props.values[id]?.source === "parser") return;
      // This pill's popup was open at mousedown, so Tippy is already closing it —
      // don't reopen. Any other pill's click still opens it (Tippy closes the
      // previously-open one).
      if (openEditorIdAtMousedown === id) return;
      editorApi(id)?.toggle();
    };

    return {
      choicesFor,
      contextIcon,
      contextText,
      contextTooltip,
      onContextPillEnter,
      relationTypeFor,
      iconFor,
      labelFor,
      selectDataFor,
      relationDataFor,
      boolDataFor,
      textDataFor,
      numberDataFor,
      onPropertyClick,
      onPropertyPillEnter,
      hideTooltip,
      onClearClick,
      showClear,
      isParserFilled,
      onPillClickCapture,
      toggleBool,
      isTextProperty,
      stringFormatFor,
      columnTypeFor,
      orderedProperties,
      visibleProperties,
      overflowProperties,
      revealProperty,
      notesTextareaEl,
      notesText,
      expandedNotesId,
      expandedNotesProperty,
      toggleNotes,
      onNotesInput,
      editingInputId,
      setInputEditorRef,
      startInputEdit,
      stopInputEdit,
      editorRef,
      toggleEditor,
    };
  },
});
</script>

<style scoped lang="scss">
// Horizontal inset aligning the pill rows with the query input's text
// ($paddingInput in styles/console.scss).
$pillsInset: 16px;

.console-pills {
  display: flex;
  flex-direction: column;
}

// The property chips sit on a single horizontally-scrollable line (no
// wrapping) at the top of the pills toolbar, above the footer bar.
.console-property-pills {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  padding: 16px $pillsInset 0 $pillsInset;
  overflow-x: auto;
  @include hide-scrollbar;
}

// The expanded Notes text area: a small box below the chips row.
.console-notes-region {
  margin: 10px $pillsInset 0 $pillsInset;
}

.console-notes-input {
  @extend %font-caption-regular;
  display: block;
  width: 100%;
  box-sizing: border-box;
  resize: none;
  border-radius: $border-radius-m;
  padding: 8px 10px;
  outline: none;
  @include transition(background-color 0.1s ease-out);
  @include themed() {
    background-color: t($bg);
    color: t($primary);
    border: none;
  }

  &:focus {
    @include themed() {
      background-color: t($bg-alt);
    }
  }

  &::placeholder {
    @include themed() {
      color: t($grey-40);
    }
  }
}

// The "…" overflow trigger: a compact chip matching the property pills.
.pill-overflow {
  flex-shrink: 0;
}

.pill--more {
  padding: 4px 8px;
}

.pill-more-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;

  :deep(.fill) {
    @include themed() {
      fill: t($grey-60);
    }
  }
}

// Base chip: an outlined, softly-rounded button — hierarchy comes from the
// border and text tone, not from a filled background.
.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  // Caption line (19px) + vertical padding (8px) + border (2px).
  height: 29px;
  padding: 4px 10px;
  border-radius: $border-radius-m;
  cursor: default;
  flex-shrink: 0;
  overflow: hidden;
  @extend %font-caption-regular;
  @include themed() {
    background-color: transparent;
    border: 1px solid t($grey-15);
    color: t($grey-80);
  }
  @include transition(
    background-color 0.1s ease-out,
    border-color 0.1s ease-out,
    color 0.1s ease-out
  );

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }
}

.pill-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;

  // Some kind icons (e.g. TaskIcon) wrap an intrinsically larger svg, so the
  // `.pill-icon` box alone doesn't shrink them; force the inner svg to match.
  :deep(svg) {
    width: 13px;
    height: 13px;
    display: block;
  }

  :deep(.fill) {
    @include themed() {
      fill: t($grey-50);
    }
  }
}

// A chip holding a value: a quiet grey fill (no accent — the value itself is
// the signal) so filled-in properties still read at a glance.
.pill--set {
  @include themed() {
    background-color: t($grey-10);
    border-color: t($grey-15);
  }

  &:hover {
    @include themed() {
      background-color: t($grey-15);
    }
  }
}

// The Notes pill while its editor is expanded below the input.
.pill--active {
  @include themed() {
    background-color: t($grey-15);
    border-color: t($grey-15);
  }
}

.pill-name {
  white-space: nowrap;
}

// The attached-context chip: a neutral pill whose color comes from the
// favicon / app icon, with an always-visible cross to detach the context.
.pill--context {
  @include themed() {
    background-color: t($grey-10);
  }
}

.pill-context-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.pill-context-text {
  max-width: 160px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

// The saved value shown next to the name on a set text pill: the value is the
// signal, so it carries the weight while the name stays quiet.
.pill-text-value {
  @extend %font-caption-medium;
  max-width: 160px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.pill-graph-bool {
  display: inline-flex;
  align-items: center;

  :deep(.graph-boolean),
  :deep(.option-property),
  :deep(.property-display),
  :deep(.property-value) {
    width: auto;
    height: auto;
  }
}

.pill-graph-string {
  display: inline-flex;
  align-items: center;

  // Collapse GraphString's full-width/height layout so the input hugs the pill
  // instead of stretching it.
  :deep(.graph-string),
  :deep(.option-property),
  :deep(.property-display),
  :deep(.property-value),
  :deep(.base-input) {
    width: auto;
    height: auto;
    min-height: 0;
    overflow: visible;
  }

  // Override GraphString's `size="lg"` field: a single compact line matching
  // the pill's caption text (`.base-input` prefix to outrank the size styles).
  :deep(.base-input .input),
  :deep(.base-input .textarea) {
    @extend %font-caption-regular;
    width: 140px;
    height: auto;
    min-height: 0;
    padding: 0;
    line-height: 1.4;
  }
}

.pill-graph-select {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-height: 19px;

  :deep(.graph-select),
  :deep(.button-menu),
  :deep(.select-field-wrapper),
  :deep(.option-property),
  :deep(.property-display),
  :deep(.property-value) {
    width: auto;
    height: auto;
    min-height: 0;
  }

  :deep(.multi-select-container) {
    gap: 4px;
    align-items: center;
  }

  :deep(.base-select-option) {
    padding-top: 0;
    padding-bottom: 0;
    line-height: 19px;
  }

  :deep(.base-select-option span) {
    line-height: 19px;
  }

  :deep(.empty) {
    white-space: nowrap;
    line-height: 19px;
    @include themed() {
      color: t($grey-50);
    }
  }
}

// The clear cross: a proper round hit target that only asserts itself on
// hover, so a set chip stays calm until the user aims at it.
.pill-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-inline-start: -1px;
  margin-inline-end: -4px;
  border-radius: 50%;
  cursor: default;
  opacity: 0.55;
  @include transition(opacity 0.1s ease-out, background-color 0.1s ease-out);

  &:hover {
    opacity: 1;
    @include themed() {
      background-color: t($grey-20);
    }
  }
}

.pill-clear-icon {
  width: 11px;
  height: 11px;
  flex-shrink: 0;

  // Follow the pill's text color (accent on set chips, neutral on the
  // destination); themed() only for specificity over icon.scss defaults.
  :deep(.fill) {
    @include themed() {
      fill: currentColor;
    }
  }
}
</style>
